const normalizeType = (value) =>
  (value || '').toString().trim().toLowerCase();

export const getPreliminaryQuestionType = (...sources) => {
  for (const source of sources) {
    if (!source) continue;

    if (source.preliminaryQuestionType) return source.preliminaryQuestionType;
    if (source.setting?.[0]?.preliminaryQuestionType) {
      return source.setting[0].preliminaryQuestionType;
    }
  }
  return null;
};

export const isDirectEliminationSurvey = (...sources) => {
  const questionType = getPreliminaryQuestionType(...sources);
  if (normalizeType(questionType) === 'direct elimination') {
    return true;
  }

  return sources.some((source) =>
    source?.preliminaries?.some((question) => question?.correctAnswer)
  );
};

export const isPreliminaryFailed = (...sources) => {
  return sources.some((source) => {
    if (!source) return false;
    const result = normalizeType(source.preliminaryResult ?? source.result);
    if (result === 'fail' || result === 'failed') return true;
    if (source.isPreliminaryFailed === true) return true;

    const steps = Array.isArray(source.surveySteps)
      ? source.surveySteps
      : source.surveySteps
        ? [source.surveySteps]
        : source.user_steps
          ? [source.user_steps]
          : [];

    return steps.some((step) => normalizeType(step?.preliminaryResult) === 'fail');
  });
};

export const hasPreliminaryQuestions = (...sources) => {
  return sources.some((source) => {
    if (!source) return false;
    if (source.preliminary === true) return true;
    return Array.isArray(source.preliminaries) && source.preliminaries.length > 0;
  });
};

export const isPreliminarySectionComplete = (
  surveyProgress,
  preliminaryCount,
  ...statusSources
) => {
  if (!preliminaryCount) return false;
  if (isPreliminaryFailed(surveyProgress, ...statusSources)) return false;

  const totalPre = Number(surveyProgress?.totalPreliminary ?? preliminaryCount);
  const completedPre = Number(surveyProgress?.preliminaryStep ?? 0);

  return totalPre > 0 && completedPre >= totalPre;
};

export const isPreliminaryAnswerFailResponse = (payload) => {
  if (!payload) return false;

  const candidates = [payload, payload?.data, payload?.result].filter(Boolean);

  for (const result of candidates) {
    const normalizedResult = normalizeType(result?.preliminaryResult ?? result?.result);

    if (normalizedResult === 'fail' || normalizedResult === 'failed') return true;
    if (result?.isCorrect === false) return true;
    if (result?.correct === false) return true;
    if (result?.passed === false) return true;
    if (result?.success === false && isPreliminaryFailErrorMessage(result?.message)) return true;

    if (isPreliminaryFailErrorMessage(result?.message)) return true;
  }

  if (isPreliminaryFailErrorMessage(payload?.message)) return true;

  return false;
};

export const isPreliminaryFailErrorMessage = (message) => {
  if (!message) return false;

  const normalized = normalizeType(message);

  return (
    normalized.includes('not correct') ||
    normalized.includes('incorrect') ||
    normalized.includes('wrong answer') ||
    normalized.includes('wrong') ||
    normalized.includes('did not pass') ||
    normalized.includes('not pass') ||
    normalized.includes('elimination') ||
    normalized.includes('non corrett') ||
    normalized.includes('sbagliat') ||
    normalized.includes('non superat') ||
    normalized.includes('risposta errata') ||
    normalized.includes('risposta non') ||
    normalized.includes('hai fallito') ||
    normalized.includes('preliminar') && normalized.includes('fail') ||
    normalized.includes('preliminar') && normalized.includes('fallit') ||
    normalized.includes('already attempted') ||
    normalized.includes('already answered') ||
    normalized.includes('gia tentat') ||
    normalized.includes('già tentat') ||
    normalized === 'fail' ||
    normalized.endsWith(' fail')
  );
};

export const getPreliminaryFailMessage = (result, error) => {
  if (result?.message) return result.message;

  const payload = result?.data || error?.response?.data;
  const candidates = [payload, payload?.data, payload?.result].filter(Boolean);

  for (const item of candidates) {
    if (item?.message) return item.message;
  }

  if (error?.message && error?.response) {
    return error.response?.data?.message || error.message;
  }

  return null;
};

export const shouldTreatPreliminarySubmitAsFail = (result, error) => {
  if (result?.failed) return true;
  if (isPreliminaryAnswerFailResponse(result?.data)) return true;
  if (isPreliminaryFailErrorMessage(result?.message)) return true;

  const responseData = error?.response?.data;
  if (isPreliminaryAnswerFailResponse(responseData)) return true;

  const errorMessage = error?.response?.data?.message || error?.message;
  return isPreliminaryFailErrorMessage(errorMessage);
};

export const hasStartedSurvey = (startSurveyData) =>
  startSurveyData?.status === 200;
